import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Pencil, Save, X } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth, isAdmin } from "../../contexts/AuthContext";
import { ListCard } from "../DevelopmentInventory";
import { FixNowStrip } from "../coach-input/FixNowStrip";

// One list field of the player's inventory snapshot, shown on its own (e.g. KPIs on Analytics, Development Priorities on Training).
export default function SnapshotListSection({ playerId, field, title, subtitle, icon, numbered, accent, withFixNow, testid }) {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [items, setItems] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.getSnapshot(playerId).then(s => setItems(s?.[field] || [])).catch(() => setItems([])); }, [playerId, field]);

  const save = async () => {
    setSaving(true);
    try {
      const saved = await api.patchSnapshotSection(playerId, "inventory", { [field]: draft });
      setItems(saved?.[field] || draft.filter(Boolean)); setDraft(null);
      toast.success(`${title} saved`);
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  if (items === null) return <div className="flex items-center gap-2 text-[#a0a0a0] text-sm py-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  const editing = draft !== null;
  const shown = editing ? draft : items;
  const addPriority = (c) => { const next = [...(editing ? draft : items), `Fix now: ${c.name}`]; setDraft(next); };

  return (
    <section className="space-y-3" data-testid={testid}>
      {admin && (
        <div className="flex justify-end gap-2">
          {editing ? <>
            <button onClick={() => setDraft(null)} className="px-3 py-1.5 border border-white/10 text-[#a0a0a0] hover:text-white text-[10px] uppercase tracking-[0.3em] font-bold flex items-center gap-1.5" data-testid={`${testid}-cancel`}><X className="w-3 h-3" /> Cancel</button>
            <button onClick={save} disabled={saving} className="px-3 py-1.5 bg-[var(--org-accent)] text-[#121212] text-[10px] uppercase tracking-[0.3em] font-bold flex items-center gap-1.5 disabled:opacity-40" data-testid={`${testid}-save`}>{saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save</button>
          </> : (
            <button onClick={() => setDraft([...items])} className="px-3 py-1.5 border border-white/10 text-[#a0a0a0] hover:text-white text-[10px] uppercase tracking-[0.3em] font-bold flex items-center gap-1.5" data-testid={`${testid}-edit`}><Pencil className="w-3 h-3" /> Edit</button>
          )}
        </div>
      )}
      {withFixNow && <FixNowStrip playerId={playerId} admin={admin} existing={shown} onAdd={addPriority} />}
      <ListCard title={title} subtitle={subtitle} items={shown} editable={editing} onChange={setDraft} testid={`${testid}-card`} icon={icon} numbered={numbered} accent={accent} />
    </section>
  );
}
